import { create } from 'zustand';
import { canRead, type StaffUser } from '../lib/accounts';
import type {
  DocumentKind,
  DocumentReviewState,
  InterviewOutcome,
  InterviewSlot,
  PartnerApplication,
} from '../lib/applications';
import type { Customer } from '../lib/customers';
import type {
  Booking,
  BookingStatus,
  Coupon,
  KitAdjustInput,
  KitFulfilInput,
  KitIssueInput,
  KitMovement,
  KitRestockRequest,
  KitsSummary,
  KitStockRow,
  KitReturnInput,
  KitType,
  KitTypeSaveInput,
  Notification,
  Professional,
  ProfessionalKits,
  Service,
  ServiceCategory,
  ServiceSaveInput,
  WarehouseItem,
  WarehouseMovement,
} from '../lib/types';
import {
  adminApi,
  type AuditEntryDto,
  type KitMovementQuery,
  type KitRequestQuery,
  type KitStockQuery,
  type ModeratedReviewDto,
  type Paged,
  type ReceiveStockInput,
  type SettingsDto,
  type StaffSaveInput,
  type WarehouseMovementQuery,
} from '../lib/admin-api';
import { fieldErrorMap } from '../lib/api';
import { byPosition } from '../lib/kits';
import { onAccessChanged, useAuth } from './auth';
import { toast } from './ui';

export type ModerationState = 'published' | 'pending' | 'hidden';

export interface ModeratedReview extends ModeratedReviewDto {}
export interface BusinessSettings extends SettingsDto {}
export interface AuditEntry extends AuditEntryDto {}

const DEFAULT_SETTINGS: BusinessSettings = {
  businessName: 'KAYRA Care',
  supportEmail: 'care@kayracare.com',
  supportPhone: '+91-90000-00000',
  taxRatePercent: 18,
  cancellationWindowHours: 4,
  sameDayLeadHours: 2,
  openingTime: '08:00',
  closingTime: '21:00',
  cities: [],
  acceptingBookings: true,
  memberDiscountPercent: 15,
  commissionRatePercent: 20,
  payoutBasis: 'total',
  minPayoutRupees: 500,
};

type Mutation = Promise<boolean>;

/**
 * Optional hooks for a mutation's failure. The store still toasts every failure;
 * these let a form also place server field errors on its inputs (`onFieldErrors`,
 * keyed by the server's field path) or keep a refusal on screen (`onError`).
 */
export interface MutationOptions {
  onFieldErrors?: (fields: Record<string, string>) => void;
  onError?: (message: string, error: unknown) => void;
}

/** A load that should not toast on failure, e.g. a background refresh. */
export interface LoadOptions {
  silent?: boolean;
}

/** A server-paged list held in the store, with the query that produced it. */
export interface RemoteList<T, Q> {
  rows: T[];
  total: number;
  query: Q;
  loading: boolean;
  loaded: boolean;
  error?: string;
}

export interface KitAttention {
  low: number;
  out: number;
}

export const DEFAULT_KIT_STOCK_QUERY: KitStockQuery = {
  professionalId: '',
  kitTypeId: '',
  kind: '',
  status: '',
  q: '',
  sort: 'attention',
  page: 1,
  pageSize: 25,
};

export const DEFAULT_KIT_MOVEMENT_QUERY: KitMovementQuery = {
  professionalId: '',
  kitTypeId: '',
  kind: '',
  jobId: '',
  page: 1,
  pageSize: 25,
};

/** Pending refill requests, oldest first: whoever has waited longest is served first. */
export const DEFAULT_KIT_REQUEST_QUERY: KitRequestQuery = {
  statuses: ['pending'],
  professionalId: '',
  sort: 'oldest',
  page: 1,
  pageSize: 50,
};

/** Decided refill requests, newest first. */
export const DEFAULT_KIT_HISTORY_QUERY: KitRequestQuery = {
  statuses: ['fulfilled', 'rejected', 'cancelled'],
  professionalId: '',
  sort: 'newest',
  page: 1,
  pageSize: 15,
};

/** The warehouse ledger, newest first. */
export const DEFAULT_WAREHOUSE_MOVEMENT_QUERY: WarehouseMovementQuery = {
  kitTypeId: '',
  professionalId: '',
  kind: '',
  page: 1,
  pageSize: 25,
};

function emptyList<T, Q>(query: Q): RemoteList<T, Q> {
  return { rows: [], total: 0, query, loading: false, loaded: false };
}

interface DbState {
  bookings: Booking[];
  services: Service[];
  categories: ServiceCategory[];
  professionals: Professional[];
  customers: Customer[];
  coupons: Coupon[];
  reviews: ModeratedReview[];
  notifications: Notification[];
  staff: StaffUser[];
  settings: BusinessSettings;
  audit: AuditEntry[];
  applications: PartnerApplication[];
  /** Every kit type, inactive ones included. Loaded with the bootstrap. */
  kitTypes: KitType[];
  /** Archived packages, loaded on demand by the Packages page's Archived filter. */
  archivedServices: Service[];
  archivedState: { loading: boolean; loaded: boolean; error?: string };
  kitSummary: KitsSummary | null;
  kitSummaryState: { loading: boolean; loaded: boolean; error?: string };
  kitStock: RemoteList<KitStockRow, KitStockQuery>;
  kitRequests: RemoteList<KitRestockRequest, KitRequestQuery>;
  kitRequestHistory: RemoteList<KitRestockRequest, KitRequestQuery>;
  kitMovements: RemoteList<KitMovement, KitMovementQuery>;
  /** Every warehouse shelf, for the Production page. */
  warehouse: WarehouseItem[];
  warehouseState: { loading: boolean; loaded: boolean; error?: string };
  warehouseMovements: RemoteList<WarehouseMovement, WarehouseMovementQuery>;
  /** Low and out cell counts per professional, for roster badges. */
  kitAttention: Record<string, KitAttention>;
  kitAttentionState: { loading: boolean; loaded: boolean; error?: string };
  /** Kit bags fetched for individual professionals, by professional id. */
  professionalKits: Record<string, ProfessionalKits>;
  /**
   * The latest ledger lines of individual professionals, by professional id, for
   * the kit bag drawer. Separate from `kitMovements`, so opening a bag never
   * changes the filters of the ledger tab.
   */
  professionalMovements: Record<string, KitMovement[]>;
  loading: boolean;
  hydrated: boolean;
  error?: string;
  hydrate: (force?: boolean) => Promise<void>;
  clear: () => void;

  setDocumentState: (id: string, kind: DocumentKind, state: DocumentReviewState, note?: string) => Mutation;
  startReview: (id: string) => Mutation;
  scheduleInterview: (id: string, slot: InterviewSlot) => Mutation;
  recordInterviewOutcome: (id: string, outcome: Omit<InterviewOutcome, 'recordedAt'>) => Mutation;
  approveApplication: (id: string) => Mutation;
  rejectApplication: (id: string, reason: string) => Mutation;
  saveApplicationNotes: (id: string, notes: string) => Mutation;

  setBookingStatus: (id: string, status: BookingStatus) => Mutation;
  /** `onCustomersBehalf`: the customer asked, or wasn't there, so their late rule applies. */
  cancelBooking: (
    id: string,
    input: { reason: string; onCustomersBehalf: boolean },
    options?: MutationOptions,
  ) => Mutation;
  reassignBooking: (id: string, professionalId: string) => Mutation;
  rescheduleBooking: (id: string, date: string, time: string) => Mutation;

  /** Creates (no id) or replaces a package. */
  saveService: (id: string | undefined, input: ServiceSaveInput, options?: MutationOptions) => Mutation;
  setServicePublished: (id: string, active: boolean, options?: MutationOptions) => Mutation;
  /** The console's "Delete". History is kept and the package can be restored. */
  archiveService: (id: string, options?: MutationOptions) => Mutation;
  restoreService: (id: string, options?: MutationOptions) => Mutation;
  loadArchivedServices: (options?: LoadOptions) => Mutation;
  upsertCategory: (category: ServiceCategory) => Mutation;
  deleteCategory: (id: string) => Mutation;

  upsertProfessional: (professional: Professional) => Mutation;
  toggleProfessionalAvailability: (id: string) => Mutation;
  updateCustomer: (id: string, patch: Partial<Customer>) => Mutation;
  upsertCoupon: (coupon: Coupon) => Mutation;
  toggleCoupon: (id: string) => Mutation;
  deleteCoupon: (id: string) => Mutation;
  setReviewState: (id: string, state: ModerationState, note?: string) => Mutation;
  sendNotification: (input: Pick<Notification, 'category' | 'title' | 'body'> & { push: boolean }) => Mutation;
  deleteNotification: (id: string) => Mutation;
  /** `accessChanged`: send the role and pages too. An administrator never sends their own. */
  upsertStaff: (user: StaffSaveInput, options?: MutationOptions & { accessChanged?: boolean }) => Mutation;
  toggleStaffActive: (id: string) => Mutation;
  resetStaffPassword: (id: string, password: string, options?: MutationOptions) => Mutation;
  updateSettings: (patch: Partial<BusinessSettings>, options?: MutationOptions) => Mutation;

  /* Kit catalogue */
  reloadKitTypes: (options?: LoadOptions) => Mutation;
  saveKitType: (id: string | undefined, input: KitTypeSaveInput, options?: MutationOptions) => Mutation;
  deactivateKitType: (id: string, options?: MutationOptions) => Mutation;

  /* Kit stock. Loaders merge the patch into the list's current query. */
  loadKitSummary: (options?: LoadOptions) => Mutation;
  loadKitStock: (patch?: Partial<KitStockQuery>, options?: LoadOptions) => Mutation;
  loadKitRequests: (patch?: Partial<KitRequestQuery>, options?: LoadOptions) => Mutation;
  loadKitRequestHistory: (patch?: Partial<KitRequestQuery>, options?: LoadOptions) => Mutation;
  loadKitMovements: (patch?: Partial<KitMovementQuery>, options?: LoadOptions) => Mutation;
  loadKitAttention: (options?: LoadOptions) => Mutation;
  loadProfessionalKits: (professionalId: string, options?: LoadOptions) => Mutation;
  loadProfessionalMovements: (professionalId: string, options?: LoadOptions) => Mutation;
  /** Re-runs every kit view that has been loaded, quietly. */
  refreshKits: () => Promise<void>;
  issueKits: (professionalId: string, input: KitIssueInput, options?: MutationOptions) => Mutation;
  adjustKit: (professionalId: string, input: KitAdjustInput, options?: MutationOptions) => Mutation;
  fulfilKitRequest: (id: string, input: KitFulfilInput, options?: MutationOptions) => Mutation;
  rejectKitRequest: (id: string, reason: string, options?: MutationOptions) => Mutation;
  returnKits: (professionalId: string, input: KitReturnInput, options?: MutationOptions) => Mutation;

  /* Warehouse */
  loadWarehouse: (options?: LoadOptions) => Mutation;
  loadWarehouseMovements: (patch?: Partial<WarehouseMovementQuery>, options?: LoadOptions) => Mutation;
  receiveStock: (kitTypeId: string, input: ReceiveStockInput, options?: MutationOptions) => Mutation;
  adjustWarehouse: (kitTypeId: string, delta: number, reason: string, options?: MutationOptions) => Mutation;
  setReorderLevel: (kitTypeId: string, level: number, options?: MutationOptions) => Mutation;
  /** Puts a fresh copy of a bag in the cache, e.g. from a counter lookup. */
  cacheProfessionalKits: (bag: ProfessionalKits) => void;
}

let hydration: Promise<void> | null = null;
let hydrationEpoch = 0;

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'The operation could not be completed.';
}

function failed(error: unknown, options?: MutationOptions): false {
  const text = message(error);
  const fields = fieldErrorMap(error);
  if (options?.onFieldErrors && Object.keys(fields).length > 0) {
    options.onFieldErrors(fields);
    toast(`${text} Check the highlighted fields.`, 'error');
  } else {
    toast(text, 'error');
  }
  options?.onError?.(text, error);
  return false;
}

/**
 * Latest-request-wins for loaders. A response is applied only if no newer load
 * of the same list started meanwhile and nobody signed out.
 */
const loadTokens = new Map<string, number>();

function beginLoad(key: string): () => boolean {
  const token = (loadTokens.get(key) ?? 0) + 1;
  loadTokens.set(key, token);
  const epoch = hydrationEpoch;
  return () => loadTokens.get(key) === token && epoch === hydrationEpoch;
}

function sortKitTypes(rows: KitType[]): KitType[] {
  return [...rows].sort(byPosition);
}

function replaceShelf(rows: WarehouseItem[], shelf: WarehouseItem): WarehouseItem[] {
  return rows.some((row) => row.kitTypeId === shelf.kitTypeId)
    ? rows.map((row) => (row.kitTypeId === shelf.kitTypeId ? shelf : row))
    : [...rows, shelf];
}

function replaceById<T extends { id: string }>(rows: T[], value: T, prepend = true): T[] {
  if (rows.some((row) => row.id === value.id)) {
    return rows.map((row) => (row.id === value.id ? value : row));
  }
  return prepend ? [value, ...rows] : [...rows, value];
}

function kitStateReset() {
  return {
    archivedServices: [] as Service[],
    archivedState: { loading: false, loaded: false },
    kitSummary: null,
    kitSummaryState: { loading: false, loaded: false },
    kitStock: emptyList<KitStockRow, KitStockQuery>(DEFAULT_KIT_STOCK_QUERY),
    kitRequests: emptyList<KitRestockRequest, KitRequestQuery>(DEFAULT_KIT_REQUEST_QUERY),
    kitRequestHistory: emptyList<KitRestockRequest, KitRequestQuery>(DEFAULT_KIT_HISTORY_QUERY),
    kitMovements: emptyList<KitMovement, KitMovementQuery>(DEFAULT_KIT_MOVEMENT_QUERY),
    warehouse: [] as WarehouseItem[],
    warehouseState: { loading: false, loaded: false } as { loading: boolean; loaded: boolean; error?: string },
    warehouseMovements: emptyList<WarehouseMovement, WarehouseMovementQuery>(DEFAULT_WAREHOUSE_MOVEMENT_QUERY),
    kitAttention: {} as Record<string, KitAttention>,
    kitAttentionState: { loading: false, loaded: false },
    professionalKits: {} as Record<string, ProfessionalKits>,
    professionalMovements: {} as Record<string, KitMovement[]>,
  };
}

/** How many ledger lines the kit bag drawer shows. */
const PROFESSIONAL_MOVEMENT_PAGE = 10;

export const useDb = create<DbState>((set, get) => {
  const replaceApplication = (application: PartnerApplication) =>
    set((state) => ({
      applications: state.applications.some((value) => value.id === application.id)
        ? state.applications.map((value) => (value.id === application.id ? application : value))
        : [application, ...state.applications],
    }));

  const refreshAudit = () => {
    const epoch = hydrationEpoch;
    void adminApi
      .audit()
      .then((audit) => {
        if (epoch === hydrationEpoch) set({ audit });
      })
      .catch(() => undefined);
  };

  const succeeded = (): true => {
    refreshAudit();
    return true;
  };

  type ListKey = 'kitStock' | 'kitRequests' | 'kitRequestHistory' | 'kitMovements' | 'warehouseMovements';
  type QueryOf<K extends ListKey> = DbState[K]['query'];
  type RowOf<K extends ListKey> = DbState[K]['rows'][number];

  /**
   * Loads one page of a server-paged list, merging `patch` into the list's
   * current query. A page that has emptied under a mutation steps back to the
   * last page that still has rows.
   */
  const loadList = async <K extends ListKey>(
    key: K,
    patch: Partial<QueryOf<K>> | undefined,
    options: LoadOptions | undefined,
    fetch: (query: QueryOf<K>) => Promise<Paged<RowOf<K>>>,
  ): Promise<boolean> => {
    const current = get()[key] as RemoteList<RowOf<K>, QueryOf<K>>;
    const query = { ...current.query, ...patch } as QueryOf<K>;
    const isCurrent = beginLoad(key);
    set({ [key]: { ...current, query, loading: true, error: undefined } } as unknown as Partial<DbState>);

    try {
      const page = await fetch(query);
      if (!isCurrent()) return true;

      const q = query as { page: number; pageSize: number };
      if (page.rows.length === 0 && q.page > 1 && page.total > 0) {
        const last = Math.max(1, Math.ceil(page.total / q.pageSize));
        return loadList(key, { ...patch, page: last } as Partial<QueryOf<K>>, options, fetch);
      }

      const next: RemoteList<RowOf<K>, QueryOf<K>> = {
        rows: page.rows,
        total: page.total,
        query,
        loading: false,
        loaded: true,
      };
      set({ [key]: next } as unknown as Partial<DbState>);
      return true;
    } catch (error) {
      if (!isCurrent()) return true;
      set((state) => ({
        [key]: { ...state[key], loading: false, error: message(error) },
      }) as unknown as Partial<DbState>);
      if (!options?.silent) toast(message(error), 'error');
      return false;
    }
  };

  /**
   * A professional's bag changed: refresh it if cached (unless the response
   * already carried it), her recent ledger lines if shown, and every loaded kit view.
   */
  const afterStockChange = (professionalId: string, bagIsFresh = false) => {
    if (!bagIsFresh && get().professionalKits[professionalId]) {
      void get().loadProfessionalKits(professionalId, { silent: true });
    }
    if (get().professionalMovements[professionalId]) {
      void get().loadProfessionalMovements(professionalId, { silent: true });
    }
    void get().refreshKits();
  };

  return {
    bookings: [],
    services: [],
    categories: [],
    professionals: [],
    customers: [],
    coupons: [],
    reviews: [],
    notifications: [],
    staff: [],
    settings: DEFAULT_SETTINGS,
    audit: [],
    applications: [],
    kitTypes: [],
    ...kitStateReset(),
    loading: false,
    hydrated: false,

    hydrate: async (force = false) => {
      if (hydration) return hydration;
      if (get().hydrated && !force) return;

      const epoch = hydrationEpoch;
      set({ loading: true, error: undefined });
      const current = adminApi
        .bootstrap(useAuth.getState().session?.user)
        .then((data) => {
          if (epoch === hydrationEpoch) {
            set({
              ...data,
              kitTypes: sortKitTypes(data.kitTypes),
              loading: false,
              hydrated: true,
              error: undefined,
            });
          }
        })
        .catch((error) => {
          if (epoch === hydrationEpoch) {
            set({ loading: false, error: message(error) });
          }
          throw error;
        })
        .finally(() => {
          if (hydration === current) hydration = null;
        });
      hydration = current;
      return current;
    },
    clear: () => {
      hydrationEpoch += 1;
      hydration = null;
      set({
        bookings: [],
        services: [],
        categories: [],
        professionals: [],
        customers: [],
        coupons: [],
        reviews: [],
        notifications: [],
        staff: [],
        audit: [],
        applications: [],
        kitTypes: [],
        ...kitStateReset(),
        settings: DEFAULT_SETTINGS,
        hydrated: false,
        loading: false,
        error: undefined,
      });
    },

    setDocumentState: async (id, kind, state, note) => {
      try {
        replaceApplication(await adminApi.reviewDocument(id, kind, state, note));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    startReview: async (id) => {
      try {
        replaceApplication(await adminApi.startReview(id));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    scheduleInterview: async (id, slot) => {
      try {
        replaceApplication(await adminApi.scheduleInterview(id, slot));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    recordInterviewOutcome: async (id, outcome) => {
      try {
        replaceApplication(await adminApi.recordOutcome(id, outcome));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    approveApplication: async (id) => {
      try {
        const application = await adminApi.approve(id);
        replaceApplication(application);
        // The new therapist joins the roster this console holds, if it holds one.
        if (application.professionalId && canRead(useAuth.getState().session?.user, 'professionals')) {
          try {
            const professional = await adminApi.professional(application.professionalId);
            set((state) => ({
              professionals: [
                professional,
                ...state.professionals.filter((value) => value.id !== professional.id),
              ],
            }));
          } catch {
            void get().hydrate(true);
          }
        }
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    rejectApplication: async (id, reason) => {
      try {
        replaceApplication(await adminApi.reject(id, reason));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    saveApplicationNotes: async (id, notes) => {
      try {
        replaceApplication(await adminApi.applicationNotes(id, notes));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },

    setBookingStatus: async (id, status) => {
      try {
        const value = await adminApi.bookingStatus(id, status);
        set((state) => ({
          bookings: state.bookings.map((item) => (item.id === id ? value : item)),
        }));
        // Status transitions reserve, release or consume kit stock.
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    cancelBooking: async (id, input, options) => {
      try {
        const value = await adminApi.cancelBooking(id, input.reason, input.onCustomersBehalf);
        set((state) => ({
          bookings: state.bookings.map((item) => (item.id === id ? value : item)),
        }));
        // Cancelling releases the kit stock the booking had reserved.
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    reassignBooking: async (id, professionalId) => {
      const booking = get().bookings.find((value) => value.id === id);
      if (!booking) return failed(new Error('This booking is no longer available.'));
      try {
        const value = await adminApi.reassignBooking(booking, professionalId);
        set((state) => ({
          bookings: state.bookings.map((item) => (item.id === id ? value : item)),
        }));
        // Reassignment moves reservations between professional kit bags.
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    rescheduleBooking: async (id, date, time) => {
      try {
        const value = await adminApi.rescheduleBooking(id, date, time);
        set((state) => ({
          bookings: state.bookings.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },

    saveService: async (id, input, options) => {
      try {
        const value = await adminApi.saveService(id, input);
        set((state) =>
          value.archived
            ? { archivedServices: replaceById(state.archivedServices, value) }
            : { services: replaceById(state.services, value) },
        );
        // Kit usage counts follow the package's bill of materials.
        void get().reloadKitTypes({ silent: true });
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    setServicePublished: async (id, active, options) => {
      try {
        const value = await adminApi.publishService(id, active);
        set((state) =>
          value.archived
            ? { archivedServices: replaceById(state.archivedServices, value) }
            : { services: replaceById(state.services, value) },
        );
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    archiveService: async (id, options) => {
      const current = get().services.find((value) => value.id === id);
      try {
        await adminApi.archiveService(id);
        set((state) => ({
          services: state.services.filter((value) => value.id !== id),
          archivedServices:
            current && state.archivedState.loaded
              ? replaceById(state.archivedServices, {
                  ...current,
                  active: false,
                  archived: true,
                  archivedAt: new Date().toISOString(),
                })
              : state.archivedServices,
        }));
        void get().reloadKitTypes({ silent: true });
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    restoreService: async (id, options) => {
      try {
        const value = await adminApi.restoreService(id);
        set((state) => ({
          archivedServices: state.archivedServices.filter((item) => item.id !== id),
          services: replaceById(state.services, value),
        }));
        void get().reloadKitTypes({ silent: true });
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    loadArchivedServices: async (options) => {
      const isCurrent = beginLoad('archivedServices');
      set((state) => ({ archivedState: { ...state.archivedState, loading: true, error: undefined } }));
      try {
        const rows = await adminApi.services('only');
        if (!isCurrent()) return true;
        set({ archivedServices: rows, archivedState: { loading: false, loaded: true } });
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        set((state) => ({
          archivedState: { ...state.archivedState, loading: false, error: message(error) },
        }));
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    upsertCategory: async (category) => {
      try {
        const value = await adminApi.saveCategory(category);
        set((state) => ({
          categories: [value, ...state.categories.filter((item) => item.id !== value.id)],
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    deleteCategory: async (id) => {
      try {
        await adminApi.deleteCategory(id);
        set((state) => ({
          categories: state.categories.filter((value) => value.id !== id),
          services: state.services.map((value) =>
            value.categoryId === id ? { ...value, active: false } : value,
          ),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },

    upsertProfessional: async (professional) => {
      try {
        const value = await adminApi.saveProfessional(professional);
        set((state) => ({
          professionals: state.professionals.map((item) =>
            item.id === value.id ? value : item,
          ),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    toggleProfessionalAvailability: async (id) => {
      const current = get().professionals.find((value) => value.id === id);
      if (!current) return failed(new Error('This therapist is no longer on the roster.'));
      try {
        const value = await adminApi.setProfessionalAvailable(id, !current.available);
        set((state) => ({
          professionals: state.professionals.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    updateCustomer: async (id, patch) => {
      try {
        const wire = await adminApi.updateCustomer(id, patch);
        const current = get().customers.find((value) => value.id === id);
        const value = { ...current, ...wire } as Customer;
        set((state) => ({
          customers: state.customers.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    upsertCoupon: async (coupon) => {
      try {
        const value = await adminApi.saveCoupon(coupon);
        set((state) => ({
          coupons: [value, ...state.coupons.filter((item) => item.id !== value.id)],
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    toggleCoupon: async (id) => {
      const current = get().coupons.find((value) => value.id === id);
      if (!current) return failed(new Error('This coupon no longer exists.'));
      try {
        const value = await adminApi.toggleCoupon(current);
        set((state) => ({
          coupons: state.coupons.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    deleteCoupon: async (id) => {
      try {
        await adminApi.deleteCoupon(id);
        set((state) => ({ coupons: state.coupons.filter((value) => value.id !== id) }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    setReviewState: async (id, state, note) => {
      try {
        const value = await adminApi.moderateReview(id, state, note);
        set((current) => ({
          reviews: current.reviews.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    sendNotification: async (input) => {
      try {
        const value = await adminApi.sendNotification(input);
        set((state) => ({ notifications: [value, ...state.notifications] }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    deleteNotification: async (id) => {
      try {
        await adminApi.deleteNotification(id);
        set((state) => ({
          notifications: state.notifications.filter((value) => value.id !== id),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    upsertStaff: async (user, options) => {
      try {
        const value = await adminApi.saveStaff(user, options?.accessChanged ?? true);
        set((state) => ({ staff: replaceById(state.staff, value) }));
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    resetStaffPassword: async (id, password, options) => {
      try {
        const value = await adminApi.resetStaffPassword(id, password);
        set((state) => ({ staff: replaceById(state.staff, value) }));
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    toggleStaffActive: async (id) => {
      const current = get().staff.find((value) => value.id === id);
      if (!current) return failed(new Error('This staff account no longer exists.'));
      try {
        const value = await adminApi.toggleStaff(current);
        set((state) => ({
          staff: state.staff.map((item) => (item.id === id ? value : item)),
        }));
        return succeeded();
      } catch (error) {
        return failed(error);
      }
    },
    updateSettings: async (patch, options) => {
      try {
        set({ settings: await adminApi.updateSettings(patch) });
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },

    /* Kit catalogue ---------------------------------------------------------- */

    reloadKitTypes: async (options) => {
      const isCurrent = beginLoad('kitTypes');
      try {
        const rows = await adminApi.kitTypes();
        if (isCurrent()) set({ kitTypes: sortKitTypes(rows) });
        return true;
      } catch (error) {
        if (!options?.silent && isCurrent()) toast(message(error), 'error');
        return false;
      }
    },
    saveKitType: async (id, input, options) => {
      try {
        const value = await adminApi.saveKitType(id, input);
        set((state) => ({ kitTypes: sortKitTypes(replaceById(state.kitTypes, value)) }));
        // Names, SKUs and thresholds are copied onto stock rows.
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    deactivateKitType: async (id, options) => {
      try {
        await adminApi.deactivateKitType(id);
        set((state) => ({
          kitTypes: state.kitTypes.map((kit) => (kit.id === id ? { ...kit, active: false } : kit)),
        }));
        void get().reloadKitTypes({ silent: true });
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },

    /* Kit stock -------------------------------------------------------------- */

    loadKitSummary: async (options) => {
      const isCurrent = beginLoad('kitSummary');
      set((state) => ({ kitSummaryState: { ...state.kitSummaryState, loading: true, error: undefined } }));
      try {
        const summary = await adminApi.kitSummary();
        if (!isCurrent()) return true;
        set({ kitSummary: summary, kitSummaryState: { loading: false, loaded: true } });
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        set((state) => ({
          kitSummaryState: { ...state.kitSummaryState, loading: false, error: message(error) },
        }));
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    loadKitStock: (patch, options) => loadList('kitStock', patch, options, adminApi.kitStock),
    loadKitRequests: (patch, options) => loadList('kitRequests', patch, options, adminApi.kitRequests),
    loadKitRequestHistory: (patch, options) =>
      loadList('kitRequestHistory', patch, options, adminApi.kitRequests),
    loadKitMovements: (patch, options) => loadList('kitMovements', patch, options, adminApi.kitMovements),
    loadKitAttention: async (options) => {
      const isCurrent = beginLoad('kitAttention');
      set((state) => ({ kitAttentionState: { ...state.kitAttentionState, loading: true, error: undefined } }));
      try {
        const rows = await adminApi.kitAttention();
        if (!isCurrent()) return true;
        const byProfessional: Record<string, KitAttention> = {};
        for (const row of rows) {
          const entry = (byProfessional[row.professionalId] ??= { low: 0, out: 0 });
          if (row.status === 'out') entry.out += 1;
          else if (row.status === 'low') entry.low += 1;
        }
        set({ kitAttention: byProfessional, kitAttentionState: { loading: false, loaded: true } });
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        set((state) => ({
          kitAttentionState: { ...state.kitAttentionState, loading: false, error: message(error) },
        }));
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    loadProfessionalKits: async (professionalId, options) => {
      const isCurrent = beginLoad(`professionalKits:${professionalId}`);
      try {
        const bag = await adminApi.professionalKits(professionalId);
        if (isCurrent()) {
          set((state) => ({ professionalKits: { ...state.professionalKits, [professionalId]: bag } }));
        }
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    loadProfessionalMovements: async (professionalId, options) => {
      const isCurrent = beginLoad(`professionalMovements:${professionalId}`);
      try {
        const page = await adminApi.kitMovements({
          ...DEFAULT_KIT_MOVEMENT_QUERY,
          professionalId,
          pageSize: PROFESSIONAL_MOVEMENT_PAGE,
        });
        if (isCurrent()) {
          set((state) => ({
            professionalMovements: { ...state.professionalMovements, [professionalId]: page.rows },
          }));
        }
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    refreshKits: async () => {
      const state = get();
      const jobs: Promise<boolean>[] = [];
      if (state.kitSummaryState.loaded) jobs.push(state.loadKitSummary({ silent: true }));
      if (state.kitStock.loaded) jobs.push(state.loadKitStock(undefined, { silent: true }));
      if (state.kitRequests.loaded) jobs.push(state.loadKitRequests(undefined, { silent: true }));
      if (state.kitRequestHistory.loaded) {
        jobs.push(state.loadKitRequestHistory(undefined, { silent: true }));
      }
      if (state.kitMovements.loaded) jobs.push(state.loadKitMovements(undefined, { silent: true }));
      if (state.kitAttentionState.loaded) jobs.push(state.loadKitAttention({ silent: true }));
      // Every issue, refill and return moves warehouse stock too.
      if (state.warehouseState.loaded) jobs.push(state.loadWarehouse({ silent: true }));
      if (state.warehouseMovements.loaded) {
        jobs.push(state.loadWarehouseMovements(undefined, { silent: true }));
      }
      await Promise.all(jobs);
    },
    cacheProfessionalKits: (bag) => {
      set((state) => ({ professionalKits: { ...state.professionalKits, [bag.professionalId]: bag } }));
    },
    returnKits: async (professionalId, input, options) => {
      try {
        const bag = await adminApi.returnKits(professionalId, input);
        set((state) => ({ professionalKits: { ...state.professionalKits, [professionalId]: bag } }));
        afterStockChange(professionalId, true);
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },

    /* Warehouse -------------------------------------------------------------- */

    loadWarehouse: async (options) => {
      const isCurrent = beginLoad('warehouse');
      set((state) => ({ warehouseState: { ...state.warehouseState, loading: true, error: undefined } }));
      try {
        const rows = await adminApi.warehouse();
        if (!isCurrent()) return true;
        set({ warehouse: rows, warehouseState: { loading: false, loaded: true } });
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        set((state) => ({
          warehouseState: { ...state.warehouseState, loading: false, error: message(error) },
        }));
        if (!options?.silent) toast(message(error), 'error');
        return false;
      }
    },
    loadWarehouseMovements: (patch, options) =>
      loadList('warehouseMovements', patch, options, adminApi.warehouseMovements),
    receiveStock: async (kitTypeId, input, options) => {
      try {
        const shelf = await adminApi.receiveStock(kitTypeId, input);
        set((state) => ({ warehouse: replaceShelf(state.warehouse, shelf) }));
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    adjustWarehouse: async (kitTypeId, delta, reason, options) => {
      try {
        const shelf = await adminApi.adjustWarehouse(kitTypeId, delta, reason);
        set((state) => ({ warehouse: replaceShelf(state.warehouse, shelf) }));
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    setReorderLevel: async (kitTypeId, level, options) => {
      try {
        const shelf = await adminApi.setReorderLevel(kitTypeId, level);
        set((state) => ({ warehouse: replaceShelf(state.warehouse, shelf) }));
        void get().refreshKits();
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    issueKits: async (professionalId, input, options) => {
      try {
        const bag = await adminApi.issueKits(professionalId, input);
        set((state) => ({ professionalKits: { ...state.professionalKits, [professionalId]: bag } }));
        afterStockChange(professionalId, true);
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    adjustKit: async (professionalId, input, options) => {
      try {
        const bag = await adminApi.adjustKit(professionalId, input);
        set((state) => ({ professionalKits: { ...state.professionalKits, [professionalId]: bag } }));
        afterStockChange(professionalId, true);
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    fulfilKitRequest: async (id, input, options) => {
      try {
        const request = await adminApi.fulfilKitRequest(id, input);
        set((state) => ({
          kitRequests: {
            ...state.kitRequests,
            rows: state.kitRequests.rows.filter((row) => row.id !== id),
            total: Math.max(0, state.kitRequests.total - (state.kitRequests.rows.some((row) => row.id === id) ? 1 : 0)),
          },
        }));
        afterStockChange(request.professionalId);
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
    rejectKitRequest: async (id, reason, options) => {
      try {
        const request = await adminApi.rejectKitRequest(id, reason);
        set((state) => ({
          kitRequests: {
            ...state.kitRequests,
            rows: state.kitRequests.rows.filter((row) => row.id !== id),
            total: Math.max(0, state.kitRequests.total - (state.kitRequests.rows.some((row) => row.id === id) ? 1 : 0)),
          },
        }));
        afterStockChange(request.professionalId);
        return succeeded();
      } catch (error) {
        return failed(error, options);
      }
    },
  };
});

// When an administrator changes this account's pages mid-session, reload what the
// console holds: lists it may now read arrive, and lists it no longer may are dropped.
onAccessChanged(() => {
  const db = useDb.getState();
  if (db.hydrated || db.loading) void db.hydrate(true).catch(() => undefined);
});
